import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import {
  heroSectionImage,
  heroSectionMobileImage,
  products,
  healthBenefits,
  deliveryInfo,
  whyChooseCornBlissImage,
  contactInfo,
} from '../../data/content';
import BrandedImage from '../../components/BrandedImage';
import ConfirmationModal from '../../components/ConfirmationModal';
import { useCart } from '../../context/CartContext';
import './HomePage.css';

function HomePage() {
  const sampleProducts = products;
  const { addToCart } = useCart();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1130);
  const productCarouselRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1130);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const heroImage = isMobile ? heroSectionMobileImage : heroSectionImage;

  const handleAddToCart = (product) => {
    if (product.price > 0) setSelectedProduct(product);
  };

  const confirmAddToCart = (quantity) => {
    if (selectedProduct) {
      addToCart({ ...selectedProduct, quantity });
      setSelectedProduct(null);
    }
  };

  const slideCarousel = (ref, direction) => {
    if (ref.current) {
      const amount = ref.current.offsetWidth;
      ref.current.scrollTo({
        left: ref.current.scrollLeft + (direction === 'prev' ? -amount : amount),
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="page-transition-wrapper">

      {selectedProduct && (
        <ConfirmationModal
          product={selectedProduct}
          onConfirm={confirmAddToCart}
          onCancel={() => setSelectedProduct(null)}
        />
      )}

      {/* ══ HERO ══ */}
      <section className="hp-hero">
        <div className="hp-hero-media">
          <img src={heroImage} alt="Corn Bliss Premium" />
        </div>

        <div className="hp-hero-content">
          <span className="hp-hero-kicker">Sri Lanka's Finest</span>

          <h1>
            Savor the <span className="highlight-gold">Gold</span> of<br />
            Sri Lankan Sunshine!
          </h1>

          <p className="hp-hero-desc">
            Pure. Natural. Preservative-Free. Fresh sweet corn from the Badulla highlands —
            delivered right to your table.
          </p>

          <div className="hp-hero-actions">
            <Link to="/more" className="hp-btn-primary">
              Order Now
            </Link>
            <a
              href={`https://wa.me/${contactInfo.whatsapp?.replace(/\s+/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hp-btn-secondary"
            >
              💬 WhatsApp Us
            </a>
          </div>

          <div className="hp-hero-stats">
            <div className="hp-stat">
              <span className="hp-stat-num">100+</span>
              <span className="hp-stat-lbl">Families Served</span>
            </div>
            <div className="hp-stat">
              <span className="hp-stat-num">0</span>
              <span className="hp-stat-lbl">Preservatives</span>
            </div>
            <div className="hp-stat">
              <span className="hp-stat-num">24/7</span>
              <span className="hp-stat-lbl">Always Open</span>
            </div>
          </div>
        </div>
      </section>

      {/* ══ PRODUCTS CAROUSEL ══ */}
      <section className="hp-products-section">
        <div className="hp-products-header">
          <span className="hp-section-label">What We Offer</span>
          <h2 className="hp-section-title">Our Ready-to-Eat Packs</h2>
        </div>

        <div className="hp-carousel-outer">
          <button
            className="hp-carousel-btn"
            onClick={() => slideCarousel(productCarouselRef, 'prev')}
            aria-label="Previous products"
          >❮</button>

          <div className="hp-carousel-track">
            <div className="hp-carousel-slider" ref={productCarouselRef}>
              {sampleProducts.map((item) => (
                <div key={item.title} className="hp-product-card">
                  <div className="hp-product-img-wrap">
                    {item.badge && (
                      <div
                        className="hp-product-badge"
                        style={{ background: `linear-gradient(135deg, ${item.badgeColor} 0%, ${item.badgeColor}cc 100%)` }}
                      >
                        {item.badge}
                      </div>
                    )}
                    <BrandedImage src={item.image} alt={item.title} imageClassName="preview-image" />
                  </div>

                  <div className="hp-product-info">
                    <h3 className="hp-product-title">{item.title}</h3>
                    <p className="hp-product-description">{item.bestFor}</p>
                  </div>

                  <div className="hp-product-footer">
                    <button
                      className="hp-add-btn"
                      onClick={() => handleAddToCart(item)}
                      disabled={item.price === 0}
                    >
                      {item.price === 0 ? 'Coming Soon' : '+ Add to Cart'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            className="hp-carousel-btn"
            onClick={() => slideCarousel(productCarouselRef, 'next')}
            aria-label="Next products"
          >❯</button>
        </div>
      </section>

      {/* ══ HEALTH BENEFITS ══ */}
      <section
        className="hp-benefits-section"
        style={{ backgroundImage: `url(${whyChooseCornBlissImage})` }}
      >
        <span className="hp-section-label">Why It's Good for You</span>
        <h2 className="hp-section-title">Why Choose Corn Bliss?</h2>

        <div className="hp-benefits-grid">
          {healthBenefits.map((benefit, i) => (
            <div key={i} className="hp-benefit-card">
              <span className="hp-benefit-icon">{benefit.icon}</span>
              <h3>{benefit.title}</h3>
              <p>{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══ DELIVERY ══ */}
      <section className="hp-delivery-section">
        <span className="hp-section-label">How We Reach You</span>
        <h2 className="hp-section-title">🚚 Delivery & Payment</h2>

        {/* Stats bar */}
        <div className="hp-delivery-stats-bar">
          <div className="hp-delivery-stat-box">
            <div className="hp-delivery-stat-icon">⚡</div>
            <div className="hp-delivery-stat-info">
              <span className="hp-delivery-stat-val">Same Day</span>
              <span className="hp-delivery-stat-lbl">Dispatch</span>
            </div>
          </div>
          <div className="hp-delivery-stat-box">
            <div className="hp-delivery-stat-icon">🌽</div>
            <div className="hp-delivery-stat-info">
              <span className="hp-delivery-stat-val">100%</span>
              <span className="hp-delivery-stat-lbl">Fresh Always</span>
            </div>
          </div>
          <div className="hp-delivery-stat-box">
            <div className="hp-delivery-stat-icon">🕐</div>
            <div className="hp-delivery-stat-info">
              <span className="hp-delivery-stat-val">24/7</span>
              <span className="hp-delivery-stat-lbl">Order Anytime</span>
            </div>
          </div>
          <div className="hp-delivery-stat-box">
            <div className="hp-delivery-stat-icon">⭐</div>
            <div className="hp-delivery-stat-info">
              <span className="hp-delivery-stat-val">Premium </span>
              <span className="hp-delivery-stat-lbl">Quality </span>
            </div>
          </div>
        </div>

        <div className="hp-delivery-grid">

          {/* Payment card */}
          <div className="hp-payment-card">
            <div className="hp-payment-card-header">
              <h3>💳 Payment Methods</h3>
              <p>Flexible options — pay the way you prefer</p>
            </div>
            <div className="hp-payment-card-body">
              <div className="hp-payment-method-row">
                <div className="hp-payment-method-icon">💵</div>
                <div className="hp-payment-method-info">
                  <span className="hp-payment-method-name">Cash on Delivery</span>
                  <span className="hp-payment-method-desc">Pay when your order arrives at your door</span>
                </div>
                <span className="hp-payment-method-pill hp-pill-green">Popular</span>
              </div>

              <div className="hp-payment-method-row">
                <div className="hp-payment-method-icon">🏦</div>
                <div className="hp-payment-method-info">
                  <span className="hp-payment-method-name">Bank Transfer</span>
                  <span className="hp-payment-method-desc">Direct deposit before dispatch</span>
                </div>
                <span className="hp-payment-method-pill hp-pill-gold">Fast</span>
              </div>

              
            </div>
          </div>

          {/* Zones card */}
          <div className="hp-zones-col">
            <div className="hp-zones-col-header">
              <h3>📍 Delivery Zones</h3>
              <p>We deliver fresh corn right to your city</p>
            </div>
            <div className="hp-zones-col-body">
              {deliveryInfo.zones.map((zone, i) => (
                <div key={i}>
                  <div className="hp-zone-block">
                    <h4>{zone.region}</h4>
                    <div className="hp-zone-tags">
                      {zone.cities.map((city, j) => (
                        <span key={j} className="hp-zone-tag">{city}</span>
                      ))}
                    </div>
                  </div>
                  {i < deliveryInfo.zones.length - 1 && (
                    <div className="hp-zone-divider" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Pricing card */}
          <div className="hp-pricing-col">
            <div className="hp-pricing-col-header">
              <h3>💰 Delivery Charges</h3>
              <p>Transparent and affordable pricing</p>
            </div>
            <div className="hp-pricing-col-body">
              <div className="hp-pricing-table">
                {deliveryInfo.pricing?.details?.map((item, i) => (
                  <div key={i} className="hp-pricing-row">
                    <span className="hp-pricing-weight">{item.weight}</span>
                    <span className="hp-pricing-amount">{item.price}</span>
                  </div>
                ))}
              </div>
              <div className="hp-pricing-note">
                <span>✨ {deliveryInfo.pricing?.description}</span>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ══ TESTIMONIALS ══ */}
      <section className="hp-testimonials-section">
        <span className="hp-section-label">Happy Customers</span>
        <h2 className="hp-section-title">What Our <em>Customers</em> Say</h2>

        {/* Auto-sliding review rows */}
        <div className="hp-reviews-wall">
          {/* Row 1 — slides left */}
          <div className="hp-review-row-wrap">
            <div className="hp-review-row hp-row-left">
              {[
                { name: 'Saymini Perera', text: 'So delicious and fresh. Going on my 2nd order just within 24 hours. Continue keeping this quality!', stars: 5, source: 'Facebook' },
                { name: 'Krishanthi Arachchige', text: 'Highly recommended. Very reliable service and very generous. Wish you all the success!', stars: 5, source: 'Facebook' },
                { name: 'Tharaka Herath', text: "Ordered three times now and it's always the best! 🌽 Ordered for Katina Ceremony and everyone loved it. Highly recommended!", stars: 5, source: 'Facebook' },
                { name: 'Koshila Liyanaarachchi', text: "Extremely satisfied. Packaging was neat, freshness and rich taste immediately noticeable. Absolutely worth the price. Will definitely order more!", stars: 5, source: 'Facebook' },
                { name: 'Kavindi Watareka', text: 'Corn kernels are super tasty, creamy and easy to make. Super convenient and clean. Delivered on time. Recommend specially for kids!', stars: 5, source: 'Facebook' },
                { name: 'Anukshini Fernando', text: 'My kids love corn bliss corn. I frequently order because they love to take it to school. Highly recommend!', stars: 5, source: 'Facebook' },
                { name: 'Rehana Azeez', text: 'Really good product, clean and well packed. Can be cooked in few minutes. We love it with butter. ❤️', stars: 5, source: 'Facebook' },
                { name: 'Dhanushka Chandimail', text: 'A really great place. Incredible discounts and wholesale pricing even in small amounts. Good customer service.', stars: 5, source: 'Google' },
                { name: 'Sandani Kumararunga', text: 'Amazing discounts on all items. Best place to buy all the domestic products at lower prices.', stars: 5, source: 'Google' },
              ].concat([
                { name: 'Saymini Perera', text: 'So delicious and fresh. Going on my 2nd order just within 24 hours. Continue keeping this quality!', stars: 5, source: 'Facebook' },
                { name: 'Krishanthi Arachchige', text: 'Highly recommended. Very reliable service and very generous. Wish you all the success!', stars: 5, source: 'Facebook' },
                { name: 'Tharaka Herath', text: "Ordered three times now and it's always the best! 🌽 Everyone loved it. Highly recommended!", stars: 5, source: 'Facebook' },
              ]).map((r, i) => (
                <div key={i} className="hp-review-card">
                  <div className="hp-review-top">
                    <div className="hp-review-avatar">{r.name.charAt(0)}</div>
                    <div className="hp-review-meta">
                      <span className="hp-review-name">{r.name}</span>
                      <div className="hp-review-stars">{'★'.repeat(r.stars)}</div>
                    </div>
                    <span className={`hp-review-source hp-review-source-${r.source.toLowerCase()}`}>
                      {r.source === 'Google' ? (
                        <svg width="11" height="11" viewBox="0 0 48 48" fill="none">
                          <path d="M44.5 20H24v8.5h11.8C34.7 33.9 30 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 6.1 1.1 8.4 3L39.8 4.4C35.6 1.5 30.6 0 24 0 12.4 0 3 9.4 3 21s9.4 21 21 21c13.3 0 21-7.5 21-21 0-1.3-.2-2.7-.5-4z" fill="#4285F4"/>
                        </svg>
                      ) : (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="#1877F2">
                          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                        </svg>
                      )}
                      {r.source}
                    </span>
                  </div>
                  <p className="hp-review-text">"{r.text}"</p>
                </div>
              ))}
            </div>
          </div>

          {/* Row 2 — slides right */}
          <div className="hp-review-row-wrap">
            <div className="hp-review-row hp-row-right">
              {[
                { name: 'Udayangani Mnh', text: 'Excellent product ❤️ Super fresh food. Organic products.', stars: 5, source: 'Facebook' },
                { name: 'Sandamal Perera', text: 'Easy to prepare. Kids love this. Local produce. Thank youuuu 🌽❤️', stars: 5, source: 'Facebook' },
                { name: 'Nilanga Ruwanpura', text: "I've bought this three times already — it's amazing! My daughter loves it.", stars: 5, source: 'Facebook' },
                { name: 'Bashini Gamage', text: '100% recommend for kids as well. Best quality product.', stars: 5, source: 'Facebook' },
                { name: 'Poornima Lakshani', text: 'ගොඩක් රසයි මේ corn ඇට. Highly Recommended Corn Bliss ❤️', stars: 5, source: 'Facebook' },
                { name: 'Sajana Wijesinghe', text: 'This sweet corn is really good, the service is also the best ❤️👍 Best value for money!', stars: 5, source: 'Facebook' },
                { name: 'Thiwanka Abeysinghe', text: 'Price is very reasonable. Extremely satisfied with quality. Staff are very friendly!', stars: 5, source: 'Facebook' },
                { name: 'Pubudu Lakshitha', text: 'Many things have wholesale prices. A vast range of products marketed by trusted names.', stars: 5, source: 'Google' },
                { name: 'Venushi Anuththara', text: 'Corn was in really good quality and fresh 🌽 Highly recommend 🤍', stars: 5, source: 'Facebook' },
              ].concat([
                { name: 'Udayangani Mnh', text: 'Excellent product ❤️ Super fresh food. Organic products.', stars: 5, source: 'Facebook' },
                { name: 'Sandamal Perera', text: 'Easy to prepare. Kids love this. Local produce. Thank youuuu 🌽❤️', stars: 5, source: 'Facebook' },
                { name: 'Nilanga Ruwanpura', text: "I've bought this three times already — it's amazing! My daughter loves it.", stars: 5, source: 'Facebook' },
              ]).map((r, i) => (
                <div key={i} className="hp-review-card">
                  <div className="hp-review-top">
                    <div className="hp-review-avatar">{r.name.charAt(0)}</div>
                    <div className="hp-review-meta">
                      <span className="hp-review-name">{r.name}</span>
                      <div className="hp-review-stars">{'★'.repeat(r.stars)}</div>
                    </div>
                    <span className={`hp-review-source hp-review-source-${r.source.toLowerCase()}`}>
                      {r.source === 'Google' ? (
                        <svg width="11" height="11" viewBox="0 0 48 48" fill="none">
                          <path d="M44.5 20H24v8.5h11.8C34.7 33.9 30 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 6.1 1.1 8.4 3L39.8 4.4C35.6 1.5 30.6 0 24 0 12.4 0 3 9.4 3 21s9.4 21 21 21c13.3 0 21-7.5 21-21 0-1.3-.2-2.7-.5-4z" fill="#4285F4"/>
                        </svg>
                      ) : (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="#1877F2">
                          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                        </svg>
                      )}
                      {r.source}
                    </span>
                  </div>
                  <p className="hp-review-text">"{r.text}"</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ SOCIAL ENGAGEMENT ══ */}
      <section className="hp-social-section">
        <span className="hp-section-label">Stay Connected</span>
        <h2 className="hp-section-title">Join the Corn Bliss Community</h2>
        <p className="hp-social-desc">
          100+ happy families served. Follow us for fresh recipes and farm updates!
        </p>

        <div className="hp-social-actions">
          <a
            href={contactInfo.social.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="hp-social-btn hp-social-btn-fb"
          >
            <svg className="hp-social-svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
            </svg>
            <span>Facebook</span>
          </a>

          <a
            href={`https://${contactInfo.social.tiktok}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hp-social-btn hp-social-btn-tk"
          >
            <svg className="hp-social-svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/>
            </svg>
            <span>TikTok</span>
          </a>

          <a
            href={contactInfo.social.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="hp-social-btn hp-social-btn-ig"
          >
            <svg className="hp-social-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
              <circle cx="12" cy="12" r="4"/>
              <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
            </svg>
            <span>Instagram</span>
          </a>
        </div>
      </section>

    </div>
  );
}

export default HomePage;